import React from 'react'

/**
 * Props for the ResponsivePicture component using Cloudinary.
 */
interface ResponsivePictureProps {
	/** The image source - can be Cloudinary public ID or URL */
	src: string
	/** Alternative text for the image */
	alt: string
	/** Image width in pixels */
	width: number
	/** Image height in pixels */
	height: number
	/** CSS class name for styling */
	className?: string
	/** Whether the image is in dark mode */
	isDark?: boolean
	/** Loading behavior - 'lazy' (default) or 'eager' for above-the-fold images */
	loading?: 'lazy' | 'eager'
	/** Image quality (1-100, default 80) */
	quality?: number
	/** Image format to convert to (default auto-detects best) */
	format?: 'webp' | 'avif' | 'jpeg' | 'png' | 'auto'
	/** Crop mode to prevent image cutting issues */
	crop?: 'fill' | 'fit' | 'pad'
	/** Gravity/focal point for cropping */
	gravity?:
		| 'auto'
		| 'center'
		| 'face'
		| 'faces'
		| 'north'
		| 'south'
		| 'east'
		| 'west'
	/** Object fit style */
	objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'
	/** Responsive image sizes */
	sizes?: string
}

type ImageSize = {
	width: number
	height: number
}

/**
 * A responsive image component powered by Cloudinary.
 *
 * This component leverages Cloudinary for instant image optimization with multiple
 * crop modes to prevent unwanted image cutting and better handle aspect ratios.
 *
 * Features:
 * - Cloudinary-powered optimization (zero server processing delay)
 * - Advanced cropping options to prevent image cutting
 * - Automatic format optimization (WebP, AVIF)
 * - Responsive image generation
 * - Smart focal point detection
 * - Global CDN delivery
 *
 * Examples:
 *
 * // Basic usage (may crop image to fit exact dimensions)
 * <ResponsivePicture src="hero-image" alt="Hero" width={800} height={400} />
 *
 * // Prevent image cutting - fit entire image within dimensions
 * <ResponsivePicture src="hero-image" alt="Hero" width={800} height={400} crop="fit" />
 *
 * // Prevent image cutting - pad to exact dimensions with background
 * <ResponsivePicture src="hero-image" alt="Hero" width={800} height={400} crop="pad" />
 *
 * // Smart cropping with face detection
 * <ResponsivePicture src="portrait" alt="Portrait" width={400} height={400} crop="fill" gravity="face" />
 *
 * // Center-focused cropping for better composition
 * <ResponsivePicture src="landscape" alt="Landscape" width={800} height={400} crop="fill" gravity="center" />
 */
function ResponsivePicture({
	src,
	alt,
	width,
	height,
	className,
	isDark = false,
	loading = 'lazy',
	quality = 80,
	format = 'auto',
	crop = 'fill',
	gravity = 'auto',
	objectFit = 'cover',
	sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
}: ResponsivePictureProps) {
	// Generate theme-aware background
	const getBackground = () => {
		return isDark ? '#1f2937' : '#f3f4f6' // gray-800 : gray-100
	}

	// Build optimized image URL using our Cloudinary resource route
	const buildImageUrl = (
		src: string,
		width: number,
		height: number,
		quality: number,
		format: string,
		crop: string,
		gravity: string,
	) => {
		const params = new URLSearchParams({
			src: src,
			w: width.toString(),
			h: height.toString(),
			q: quality.toString(),
			f: format,
			crop: crop,
			gravity: gravity,
		})
		return `/resources/image-optimizer?${params.toString()}`
	}

	// Generate responsive srcSet for different screen densities and sizes
	const generateSrcSet = () => {
		// For full-screen backgrounds, use viewport-based sizes
		const isFullScreen: boolean =
			sizes === '100vw' ||
			(className?.includes('inset-0') ?? false) ||
			(className?.includes('h-screen') ?? false)

		const imageSizes: ImageSize[] = isFullScreen
			? [
					{ width: 768, height: Math.round(768 * (height / width)) }, // Mobile
					{ width: 1024, height: Math.round(1024 * (height / width)) }, // Tablet
					{ width: 1366, height: Math.round(1366 * (height / width)) }, // Laptop
					{ width: 1920, height: Math.round(1920 * (height / width)) }, // Desktop
					{ width: 2560, height: Math.round(2560 * (height / width)) }, // Large Desktop
				]
			: [
					{ width: Math.round(width * 0.5), height: Math.round(height * 0.5) }, // 0.5x
					{
						width: Math.round(width * 0.75),
						height: Math.round(height * 0.75),
					}, // 0.75x
					{ width, height }, // 1x
					{
						width: Math.round(width * 1.25),
						height: Math.round(height * 1.25),
					}, // 1.25x
					{ width: Math.round(width * 1.5), height: Math.round(height * 1.5) }, // 1.5x
				]

		return imageSizes
			.map((size: ImageSize) => {
				const url = buildImageUrl(
					src,
					size.width,
					size.height,
					quality,
					format,
					crop,
					gravity,
				)
				return `${url} ${size.width}w`
			})
			.join(', ')
	}

	// Generate picture element with multiple formats for better browser support
	const generatePictureSources = () => {
		const formats = format === 'auto' ? ['avif', 'webp', 'jpeg'] : [format]

		return formats.map((fmt) => {
			const isFullScreen: boolean =
				sizes === '100vw' ||
				(className?.includes('inset-0') ?? false) ||
				(className?.includes('h-screen') ?? false)

			const sizesToUse: ImageSize[] = isFullScreen
				? [
						{ width: 768, height: Math.round(768 * (height / width)) },
						{ width: 1024, height: Math.round(1024 * (height / width)) },
						{ width: 1366, height: Math.round(1366 * (height / width)) },
						{ width: 1920, height: Math.round(1920 * (height / width)) },
						{ width: 2560, height: Math.round(2560 * (height / width)) },
					]
				: [
						{
							width: Math.round(width * 0.5),
							height: Math.round(height * 0.5),
						},
						{
							width: Math.round(width * 0.75),
							height: Math.round(height * 0.75),
						},
						{ width, height },
						{
							width: Math.round(width * 1.25),
							height: Math.round(height * 1.25),
						},
						{
							width: Math.round(width * 1.5),
							height: Math.round(height * 1.5),
						},
					]

			const srcSet = sizesToUse
				.map((size: ImageSize) => {
					const url = buildImageUrl(
						src,
						size.width,
						size.height,
						quality,
						fmt,
						crop,
						gravity,
					)
					return `${url} ${size.width}w`
				})
				.join(', ')

			const mimeType = fmt === 'jpeg' ? 'image/jpeg' : `image/${fmt}`

			return {
				srcSet,
				type: mimeType,
				format: fmt,
			}
		})
	}

	// Main image URL (1x fallback)
	const optimizedSrc = buildImageUrl(
		src,
		width,
		height,
		quality,
		format === 'auto' ? 'jpeg' : format,
		crop,
		gravity,
	)

	// Container styles with theme-aware background
	const containerStyles: React.CSSProperties = {
		backgroundColor: getBackground(),
	}

	// Image styles for proper full-screen coverage
	const imageStyles: React.CSSProperties = {
		objectFit,
		width: '100%',
		height: '100%',
		minWidth: '100%',
		minHeight: '100%',
	}

	const sources = generatePictureSources()

	return (
		<div className={`${className || ''}`} style={containerStyles}>
			<picture className="block h-full w-full">
				{/* Modern formats first */}
				{sources.slice(0, -1).map((source) => (
					<source
						key={source.format}
						srcSet={source.srcSet}
						sizes={sizes}
						type={source.type}
					/>
				))}

				{/* Fallback image */}
				<img
					src={optimizedSrc}
					srcSet={sources[sources.length - 1]?.srcSet || generateSrcSet()}
					sizes={sizes}
					alt={alt}
					width={width}
					height={height}
					loading={loading}
					style={imageStyles}
					className="block h-full w-full object-cover"
					onError={() => {
						console.error('Failed to load optimized image:', src)
					}}
				/>
			</picture>
		</div>
	)
}

export default ResponsivePicture
